'use client';

import { Button } from '@/components/Button/ButtonV2';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/Dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, Info, HelpCircle, CheckCircle, X, ArrowRight, ArrowLeft } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useCustomerJourney } from '@/containers/CustomerJourneySetup/context/setup';
import { PlanType } from '@/containers/CustomerJourneySetup/context/types';

interface ScheduleGuideProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function DialogGuide({ onClose, isOpen }: ScheduleGuideProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const totalSteps = 4;
  const { state } = useCustomerJourney();
  const isPRO = state.plan === PlanType.PRO;

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const steps = [
    {
      icon: <Calendar className="h-20 w-20 text-primary" />,
      title: 'Horarios recurrentes semanales',
      description: isPRO
        ? 'Estás configurando tus horarios habituales de trabajo que se repetirán automáticamente cada semana.'
        : 'Estás configurando los horarios habituales de trabajo de cada profesional que se repetirán automáticamente cada semana.'
    },
    {
      icon: <Clock className="h-20 w-20 text-primary" />,
      title: '¿Cómo funcionan?',
      content: (
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-gradient-to-r from-blue/10 to-blue/30 rounded-lg border-l-4 border-primary">
            <CheckCircle className="h-6 w-6 text-primary mt-0.5 flex-shrink-0" />
            <p className="text-sm">
              Estos horarios determinan{' '}
              {isPRO ? (
                <strong>cuándo estás disponible</strong>
              ) : (
                <strong>cuándo está disponible cada profesional</strong>
              )}{' '}
              para brindar servicios.
            </p>
          </div>
          <div className="flex items-start gap-3 p-3 bg-gradient-to-r from-blue/10 to-blue/30 rounded-lg border-l-4 border-primary">
            <CheckCircle className="h-6 w-6 text-primary mt-0.5 flex-shrink-0" />
            <p className="text-sm">
              Se <strong>repiten automáticamente cada semana</strong> sin necesidad de configurarlos
              nuevamente.
            </p>
          </div>
          <div className="flex items-start gap-3 p-3 bg-gradient-to-r from-blue/10 to-blue/30 rounded-lg border-l-4 border-primary">
            <CheckCircle className="h-6 w-6 text-primary mt-0.5 flex-shrink-0" />
            <p className="text-sm">
              Puedes configurar <strong>diferentes horarios para cada día</strong> de la semana.
            </p>
          </div>
        </div>
      )
    },
    {
      icon: <Info className="h-20 w-20 text-blue" />,
      title: '¿Qué pasa con las excepciones?',
      content: (
        <div className="space-y-4">
          <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue/10 to-blue/30 p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <Info className="h-6 w-6" />
              <div>
                <p className="font-medium text-blue">Días libres y vacaciones</p>
                <p className="mt-1 text-sm text-blue/80">
                  Podrás configurar días libres, vacaciones y ausencias desde el panel de control.
                </p>
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-50 to-green-100 p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <Calendar className="h-6 w-6" />
              <div>
                <p className="font-medium text-green-800">Horarios especiales</p>
                <p className="mt-1 text-sm text-green-700">
                  También podrás añadir horarios especiales para fechas específicas que no se repitan
                  semanalmente.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      icon: <CheckCircle className="h-20 w-20 text-green-500" />,
      title: '¡Listo para comenzar!',
      description: isPRO
        ? 'Ahora puedes configurar tus horarios recurrentes de trabajo. Recuerda que podrás modificarlos en cualquier momento desde el panel de control.'
        : 'Ahora puedes configurar los horarios recurrentes de cada profesional. Recuerda que podrás modificarlos en cualquier momento desde el panel de control.'
    }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-w-[90vw] max-h-[75vh] sm:max-h-[85vh] flex flex-col mx-auto rounded-md">
        <DialogHeader className="pb-4 border-b flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <HelpCircle className="h-5 w-5 text-primary" />
            Guía de configuración de horarios
          </DialogTitle>
        </DialogHeader>

        <div className="py-6 px-2 overflow-hidden flex-1">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex flex-col min-h-[30vh] sm:min-h-[40vh]">
                <div className="flex flex-col items-center mb-6 sm:mb-8">
                  {currentStep !== 1 && currentStep !== 2 && (
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.5, delay: 0.1 }}
                      className="p-4 rounded-full bg-gray-50 shadow-inner mb-4 sm:mb-6 border"
                    >
                      {steps[currentStep].icon}
                    </motion.div>
                  )}

                  <motion.h3
                    className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-3 sm:mb-4"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                  >
                    {steps[currentStep].title}
                  </motion.h3>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.3 }}
                  className="flex-1"
                >
                  {steps[currentStep].content ? (
                    steps[currentStep].content
                  ) : (
                    <p className="text-base text-gray-600 text-center leading-relaxed">
                      {steps[currentStep].description}
                    </p>
                  )}
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <DialogFooter className="flex !flex-row gap-3 sm:gap-4 justify-between pt-4 sm:pt-6 border-t flex-shrink-0">
          {currentStep > 0 ? (
            <Button
              variant="outline"
              onClick={handlePrevious}
              className="flex-1 !text-base bg-white shadow-sm hover:bg-gray-50 h-14 sm:h-12 rounded-xl"
              size="lg"
            >
              <div className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Anterior
              </div>
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 !text-base bg-white shadow-sm hover:bg-gray-50 h-14 sm:h-12 rounded-xl"
              size="lg"
            >
              <div className="flex items-center gap-2">
                <X className="h-4 w-4" />
                Cerrar
              </div>
            </Button>
          )}
          {currentStep < totalSteps - 1 ? (
            <Button
              onClick={handleNext}
              className="flex-1 !text-base shadow-md hover:shadow-lg transition-all h-14 sm:h-12 rounded-xl bg-blue hover:bg-blue/90"
              size="lg"
            >
              <div className="flex items-center gap-2">
                Siguiente
                <ArrowRight className="h-4 w-4" />
              </div>
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="flex-1 !text-base shadow-md hover:shadow-lg transition-all h-14 sm:h-12 rounded-xl bg-blue hover:bg-blue/90"
              size="lg"
            >
              Entendido
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
